import { db } from './firebase'
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, orderBy, limit, serverTimestamp, increment } from 'firebase/firestore'
import specialAccessService from './specialAccessService'
import organizationService from './organizationService'

class SubscriptionService {
  constructor() {
    this.pricingTiers = {
        free: {
          name: 'Free',
          price: 0,
          maxAdmins: 1,
          maxRegularAdmins: 0,
          maxGroups: 1,
          maxSubGroups: 0,
          maxAlertsPerMonth: 3,
          emailAlerts: false,
          pinnedMapping: false,
          calendarAccess: false,
          analyticsAccess: false,
          features: ['1 organization admin', '1 group', '3 alerts per month', 'Basic push notifications', 'No email alerts', 'No pinned mapping', 'No calendar posting', 'No analytics']
        },
        pro_trial: {
          name: 'Premium Trial',
          price: 0,
          maxAdmins: 3,
          maxGroupLeaders: 5,
          maxGroups: 20,
          maxSubGroups: 1,
          maxAlertsPerMonth: 500,
          privateGroups: true,
          pinnedMapping: true,
          emailAlerts: true,
          calendarAccess: true,
          analyticsAccess: true,
          isTrial: true,
          features: ['3 organization admins', '5 group leaders (can post to assigned groups)', '20 groups', '1 sub-group per group', '500 alerts per month', 'Private groups', 'Pinned mapping', 'Calendar posting', 'Analytics dashboard', 'Email and push notifications', 'Email support', 'Full web browser access', 'Mobile app access']
        },
        pro: {
          name: 'Pro',
          price: 10,
          monthlyPrice: 10,
          yearlyPrice: 100,
          maxAdmins: 2,
          maxGroupLeaders: 1,
          maxGroups: 5,
          maxSubGroups: 0,
          maxAlertsPerMonth: 100,
          privateGroups: false,
          pinnedMapping: false,
          emailAlerts: false,
          calendarAccess: false,
          analyticsAccess: false,
          features: ['2 organization admins', '1 group leader (can post to assigned groups)', '5 groups', '100 alerts per month', 'No pinned mapping', 'No email alerts', 'No calendar posting', 'No analytics', 'Full web browser access', 'Advanced push notifications', 'Email support', 'Mobile app access']
        },
        premium: {
          name: 'Premium',
          price: 25,
          monthlyPrice: 25,
          yearlyPrice: 250,
          maxAdmins: 3,
          maxGroupLeaders: 5,
          maxGroups: 20,
          maxSubGroups: 1,
          maxAlertsPerMonth: 500,
          privateGroups: true,
          pinnedMapping: true,
          emailAlerts: true,
          calendarAccess: true,
          analyticsAccess: true,
          features: ['3 organization admins', '5 group leaders (can post to assigned groups)', '20 groups', '1 sub-group per group', '500 alerts per month', 'Private groups', 'Pinned mapping', 'Calendar posting', 'Analytics dashboard', 'Email and push notifications', 'Email support', 'Full web browser access', 'Mobile app access']
        },
        enterprise: {
          name: 'Enterprise',
          price: 50,
          monthlyPrice: 50,
          yearlyPrice: 500,
          maxAdmins: 3,
          maxGroupLeaders: 'custom',
          maxGroups: 'custom',
          maxSubGroups: 'custom',
          maxAlertsPerMonth: 999999,
          privateGroups: true,
          pinnedMapping: true,
          emailAlerts: true,
          calendarAccess: true,
          analyticsAccess: true,
          features: ['3 organization admins', 'Custom group leaders (can post to assigned groups)', 'Custom groups', 'Custom sub-groups', 'Custom alerts', 'Private groups', 'Pinned mapping', 'Calendar posting', 'Analytics dashboard', 'Email and push notifications', 'Email support', 'Full web browser access', 'Mobile app access']
        }
    }
  }

  // Get pricing tier information
  getPricingTier(tierName) {
    return this.pricingTiers[tierName] || this.pricingTiers.free
  }

  // Check if email alerts are allowed for a subscription tier
  areEmailAlertsAllowed(subscription) {
    if (!subscription) return false
    
    const effectiveLimits = this.getEffectiveLimits(subscription)
    const emailAlertsAllowed = effectiveLimits.emailAlerts === true
    
    console.log('🔧 Email alerts check:', {
      planType: subscription.planType,
      emailAlerts: effectiveLimits.emailAlerts,
      allowed: emailAlertsAllowed
    })
    
    return emailAlertsAllowed
  }

  // Check if private groups are allowed for a subscription tier
  arePrivateGroupsAllowed(subscription) {
    if (!subscription) return false
    
    const effectiveLimits = this.getEffectiveLimits(subscription)
    return effectiveLimits.privateGroups === true
  }

  // Check if pinned mapping is allowed for a subscription tier
  isPinnedMappingAllowed(subscription) {
    if (!subscription) return false
    
    const effectiveLimits = this.getEffectiveLimits(subscription)
    return effectiveLimits.pinnedMapping === true
  }

  // Check if calendar access is allowed for a subscription tier
  isCalendarAccessAllowed(subscription) {
    if (!subscription) return false
    
    const effectiveLimits = this.getEffectiveLimits(subscription)
    return effectiveLimits.calendarAccess === true
  }

  // Check if analytics access is allowed for a subscription tier
  isAnalyticsAccessAllowed(subscription) {
    if (!subscription) return false
    
    const effectiveLimits = this.getEffectiveLimits(subscription)
    return effectiveLimits.analyticsAccess === true
  }

  // Get all pricing tiers
  getAllPricingTiers() {
    return this.pricingTiers
  }

  // Create or update user subscription
  async createOrUpdateSubscription(userId, subscriptionData) {
      try {
        
        const planType = subscriptionData.planType || 'free'
        const tier = this.pricingTiers[planType] || this.pricingTiers.free
        
        const subscription = {
          userId,
          stripeCustomerId: subscriptionData.customerId,
          stripeSubscriptionId: subscriptionData.subscriptionId,
          planType,
          status: subscriptionData.status || 'active',
          currentPeriodStart: subscriptionData.currentPeriodStart,
          currentPeriodEnd: subscriptionData.currentPeriodEnd,
          cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd || false,
          // Include subscription limits
          maxAdmins: tier.maxAdmins,
          maxRegularAdmins: tier.maxRegularAdmins || 0,
          maxGroups: tier.maxGroups,
          maxSubGroups: tier.maxSubGroups || 0,
          maxAlertsPerMonth: tier.maxAlertsPerMonth,
          createdAt: subscriptionData.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp()
        }

      // Store in user subcollection
      const subscriptionRef = doc(db, 'users', userId, 'subscriptions', subscriptionData.subscriptionId || 'current')
      await setDoc(subscriptionRef, subscription)
      
      // Also update user profile for quick access
      await updateDoc(doc(db, 'users', userId), {
        planType: subscription.planType,
        subscriptionId: subscription.stripeSubscriptionId,
        stripeCustomerId: subscription.stripeCustomerId,
        updatedAt: serverTimestamp()
      })
      
      
      return subscription
    } catch (error) {
      console.error('❌ SubscriptionService: Error creating/updating subscription:', error)
      throw error
    }
  }

  // Get user subscription
  async getUserSubscription(userId) {
    try {
      
      // Platform admin bypass - check if user is platform admin
      const platformAdminUIDs = ['YnFxDZkOteWCNr49v49g9AVqA9m2']
      if (platformAdminUIDs.includes(userId)) {
        return {
          userId,
          planType: 'platform_admin',
          accessLevel: 'platform_admin',
          status: 'active',
          name: 'Platform Admin',
          price: 0,
          period: 'month',
          description: 'Unlimited access to all platform features',
          features: [
            'Unlimited alerts',
            'Unlimited groups', 
            'Unlimited admins',
            'Full platform access',
            'All features enabled',
            'Platform management tools'
          ],
          limits: {
            admins: -1, // Unlimited
            groups: -1, // Unlimited
            alerts: -1 // Unlimited
          },
          platformAdmin: true
        }
      }
      
      // First check user profile for planType and billingPeriod (this is the source of truth)
      const userDoc = await getDoc(doc(db, 'users', userId))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        console.log('🔍 User profile data:', {
          accessLevel: userData.accessLevel,
          planType: userData.planType,
          subscriptionPlan: userData.subscriptionPlan,
          subscriptionStatus: userData.subscriptionStatus,
          subscriptionType: userData.subscriptionType,
          billingPeriod: userData.billingPeriod,
          isOrganizationAdmin: userData.isOrganizationAdmin,
          email: userData.email
        })
        
        console.log('🔍 All user data keys:', Object.keys(userData))
        console.log('🔍 User data values:', userData)
        
        // Check for standard access level first
        if (userData.accessLevel === 'standard') {
          return {
            userId,
            planType: 'standard',
            accessLevel: 'standard',
            status: 'active',
            name: 'Standard',
            price: 0,
            period: 'month',
            description: 'Basic access for individual users',
            features: [
              'View alerts from followed organizations',
              'Join groups as a member',
              'Discover organizations',
              'Basic push notifications',
              'Email support'
            ],
            limits: {
              admins: 0,
              groups: 0,
              alerts: 0
            }
          }
        }
        
        // Check for Pro trial (legacy)
        if (userData.accessLevel === 'pro' && userData.subscriptionStatus === 'trial') {
          return {
            userId,
            planType: 'premium',
            accessLevel: 'premium',
            status: 'trial',
            subscriptionType: 'premium',
            trialStartDate: userData.trialStartDate,
            trialEndDate: userData.trialEndDate,
            trialDaysRemaining: userData.trialDaysRemaining,
            ...this.pricingTiers.premium
          }
        }
        
        // Check for Premium trial
        if (userData.accessLevel === 'premium' && userData.subscriptionStatus === 'trial') {
          return {
            userId,
            planType: 'premium',
            accessLevel: 'premium',
            status: 'trial',
            subscriptionType: 'premium',
            trialStartDate: userData.trialStartDate,
            trialEndDate: userData.trialEndDate,
            trialDaysRemaining: userData.trialDaysRemaining,
            ...this.pricingTiers.premium
          }
        }
        
        // Check for other plan types (premium, enterprise, etc.) - but only if not in trial
        if (userData.planType && userData.planType !== 'free' && userData.subscriptionStatus !== 'trial') {
          console.log('🔍 planType check passed, planType:', userData.planType, 'is not free')
          console.log('🔍 User profile billing period:', userData.billingPeriod)
          const subscriptionData = {
            userId,
            planType: userData.planType,
            status: 'active',
            billingPeriod: userData.billingPeriod, // Include billing period
            ...this.pricingTiers[userData.planType] || this.pricingTiers.free
          }
          console.log('🔍 Returning subscription data:', {
            planType: subscriptionData.planType,
            billingPeriod: subscriptionData.billingPeriod,
            monthlyPrice: subscriptionData.monthlyPrice,
            yearlyPrice: subscriptionData.yearlyPrice,
            price: subscriptionData.price
          })
          console.log('🔍 Full subscription data being returned:', subscriptionData)
          return subscriptionData
        }
        
        // Check if user has subscription data in other fields
        if (userData.subscriptionPlan && userData.subscriptionPlan !== 'free') {
          console.log('🔍 subscriptionPlan check passed, subscriptionPlan:', userData.subscriptionPlan, 'is not free')
          const subscriptionData = {
            userId,
            planType: userData.subscriptionPlan,
            status: userData.subscriptionStatus || 'active',
            billingPeriod: userData.billingPeriod,
            ...this.pricingTiers[userData.subscriptionPlan] || this.pricingTiers.free
          }
          console.log('🔍 Returning subscription data from subscriptionPlan:', {
            planType: subscriptionData.planType,
            billingPeriod: subscriptionData.billingPeriod,
            status: subscriptionData.status
          })
          console.log('🔍 Full subscription data being returned:', subscriptionData)
          return subscriptionData
        }
        
        // Fallback: if user is not an organization admin and not platform admin, treat as standard
        const isPlatformAdmin = userId === 'YnFxDZkOteWCNr49v49g9AVqA9m2' || 
                               userData.email === 'jed@chimeo.app'
        
        console.log('🔍 Fallback check - isOrganizationAdmin:', userData.isOrganizationAdmin, 'isPlatformAdmin:', isPlatformAdmin)
        
        if (!userData.isOrganizationAdmin && !isPlatformAdmin) {
          return {
            userId,
            planType: 'standard',
            accessLevel: 'standard',
            status: 'active',
            name: 'Standard',
            price: 0,
            period: 'month',
            description: 'Basic access for individual users',
            features: [
              'View alerts from followed organizations',
              'Join groups as a member',
              'Discover organizations',
              'Basic push notifications',
              'Email support'
            ],
            limits: {
              admins: 0,
              groups: 0,
              alerts: 0
            }
          }
        }
      }
      
      // Fallback: try to get from user subcollection (only if no user profile data)
      console.log('🔍 No user profile data found, checking subcollection...')
      const subscriptionQuery = query(
        collection(db, 'users', userId, 'subscriptions'),
        orderBy('createdAt', 'desc'),
        limit(1)
      )
      
      const subscriptionSnapshot = await getDocs(subscriptionQuery)
      
      if (!subscriptionSnapshot.empty) {
        const subscriptionDoc = subscriptionSnapshot.docs[0]
        const subscription = subscriptionDoc.data()
        console.log('🔍 Subscription data from subcollection:', {
          planType: subscription.planType,
          billingPeriod: subscription.billingPeriod,
          status: subscription.status
        })
        return subscription
      }
      
      return {
        userId,
        planType: 'free',
        status: 'active',
        ...this.pricingTiers.free
      }
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting subscription:', error)
      throw error
    }
  }

  // Get organization subscription (for organization-level features)
  async getOrganizationSubscription(organizationId) {
    try {
      const orgId = organizationService.coerceOrganizationId(organizationId) ||
        (typeof organizationId === 'string' ? organizationId.trim() : null)
      if (!orgId) {
        const tier = this.pricingTiers.free
        return {
          organizationId: null,
          planType: 'free',
          status: 'active',
          ...tier,
          limits: {
            admins: tier.maxAdmins === 'custom' ? 'custom' : tier.maxAdmins,
            regularAdmins: tier.maxRegularAdmins === 'custom' ? 'custom' : (tier.maxRegularAdmins || 0),
            groups: tier.maxGroups === 'custom' ? 'custom' : tier.maxGroups,
            subGroups: tier.maxSubGroups === 'custom' ? 'custom' : (tier.maxSubGroups || 0),
            alerts: tier.maxAlertsPerMonth
          }
        }
      }

      const normalizePlanKey = (v) => {
        if (v == null) return null
        if (typeof v !== 'string') return null
        const t = v.trim().toLowerCase()
        return t.length ? t : null
      }

      const orgDoc = await getDoc(doc(db, 'organizations', orgId))
      
      if (orgDoc.exists()) {
        const orgData = orgDoc.data()
        const rawPlan = orgData.planType || orgData.subscriptionPlan || orgData.plan || orgData.tier
        const docPlanType = normalizePlanKey(rawPlan)
        
        if (docPlanType && docPlanType !== 'free') {
          
          const subscriptionId = orgData.subscriptionId
          if (subscriptionId) {
            const orgSubscriptionDoc = await getDoc(doc(db, 'organizations', orgId, 'subscriptions', subscriptionId))
            if (orgSubscriptionDoc.exists()) {
              const subscription = orgSubscriptionDoc.data()
              const subPlanKey = normalizePlanKey(subscription.planType) || docPlanType
              
              const tier = this.pricingTiers[subPlanKey] || this.pricingTiers.free
              const effectiveLimits = this.getEffectiveLimits(subscription)
              const limits = {
                admins: subscription.maxAdmins !== undefined ? subscription.maxAdmins : (effectiveLimits.maxAdmins === 'custom' ? 'custom' : effectiveLimits.maxAdmins),
                regularAdmins: subscription.maxRegularAdmins !== undefined ? subscription.maxRegularAdmins : (effectiveLimits.maxRegularAdmins === 'custom' ? 'custom' : (effectiveLimits.maxRegularAdmins || 0)),
                groups: subscription.maxGroups !== undefined ? subscription.maxGroups : (effectiveLimits.maxGroups === 'custom' ? 'custom' : effectiveLimits.maxGroups),
                subGroups: subscription.maxSubGroups !== undefined ? subscription.maxSubGroups : (effectiveLimits.maxSubGroups === 'custom' ? 'custom' : (effectiveLimits.maxSubGroups || 0)),
                alerts: subscription.maxAlertsPerMonth !== undefined ? subscription.maxAlertsPerMonth : effectiveLimits.maxAlertsPerMonth
              }
              
              return {
                organizationId: orgId,
                planType: subPlanKey,
                status: subscription.status || 'active',
                billingPeriod: subscription.billingPeriod || orgData.billingPeriod,
                ...tier,
                limits
              }
            }
          }
          
          const tier = this.pricingTiers[docPlanType] || this.pricingTiers.free
          const effectiveLimits = this.getEffectiveLimits(orgData)
          const limits = {
            admins: orgData.maxAdmins !== undefined ? orgData.maxAdmins : (effectiveLimits.maxAdmins === 'custom' ? 'custom' : effectiveLimits.maxAdmins),
            regularAdmins: orgData.maxRegularAdmins !== undefined ? orgData.maxRegularAdmins : (effectiveLimits.maxRegularAdmins === 'custom' ? 'custom' : (effectiveLimits.maxRegularAdmins || 0)),
            groups: orgData.maxGroups !== undefined ? orgData.maxGroups : (effectiveLimits.maxGroups === 'custom' ? 'custom' : effectiveLimits.maxGroups),
            subGroups: orgData.maxSubGroups !== undefined ? orgData.maxSubGroups : (effectiveLimits.maxSubGroups === 'custom' ? 'custom' : (effectiveLimits.maxSubGroups || 0)),
            alerts: orgData.maxAlertsPerMonth !== undefined ? orgData.maxAlertsPerMonth : effectiveLimits.maxAlertsPerMonth
          }
          
          return {
            organizationId: orgId,
            planType: docPlanType,
            status: 'active',
            billingPeriod: orgData.billingPeriod,
            ...tier,
            limits
          }
        }
        
        const subscriptionId = orgData.subscriptionId
        if (subscriptionId) {
          const orgSubscriptionDoc = await getDoc(doc(db, 'organizations', orgId, 'subscriptions', subscriptionId))
          if (orgSubscriptionDoc.exists()) {
            const subscription = orgSubscriptionDoc.data()
            const subPlanKey = normalizePlanKey(subscription.planType)
            
            const tier = this.pricingTiers[subPlanKey] || this.pricingTiers.free
            const effectiveLimits = this.getEffectiveLimits(subscription)
            const limits = {
              admins: subscription.maxAdmins !== undefined ? subscription.maxAdmins : (effectiveLimits.maxAdmins === 'custom' ? 'custom' : effectiveLimits.maxAdmins),
              regularAdmins: subscription.maxRegularAdmins !== undefined ? subscription.maxRegularAdmins : (effectiveLimits.maxRegularAdmins === 'custom' ? 'custom' : (effectiveLimits.maxRegularAdmins || 0)),
              groups: subscription.maxGroups !== undefined ? subscription.maxGroups : (effectiveLimits.maxGroups === 'custom' ? 'custom' : effectiveLimits.maxGroups),
              subGroups: subscription.maxSubGroups !== undefined ? subscription.maxSubGroups : (effectiveLimits.maxSubGroups === 'custom' ? 'custom' : (effectiveLimits.maxSubGroups || 0)),
              alerts: subscription.maxAlertsPerMonth !== undefined ? subscription.maxAlertsPerMonth : effectiveLimits.maxAlertsPerMonth
            }
            
            return {
              organizationId: orgId,
              planType: subPlanKey || subscription.planType,
              status: subscription.status || 'active',
              ...tier,
              limits
            }
          }
          
          // Then try to get from subscriptions collection
          const subscriptionDoc = await getDoc(doc(db, 'subscriptions', subscriptionId))
          if (subscriptionDoc.exists()) {
            const subscription = subscriptionDoc.data()
            return subscription
          }
          
          // If not found in subscriptions collection, check if it's a user ID
          // and get the user's subscription from their subcollection
          const adminIds = orgData.adminIds || {}
          const adminUserIds = Object.keys(adminIds).filter(id => adminIds[id] === true)
          
          // Check each admin user's subscriptions
          for (const userId of adminUserIds) {
            const userSubscriptionDoc = await getDoc(doc(db, 'users', userId, 'subscriptions', subscriptionId))
            if (userSubscriptionDoc.exists()) {
              const subscription = userSubscriptionDoc.data()
              const subPlanKey = normalizePlanKey(subscription.planType)
              const tier = this.pricingTiers[subPlanKey] || this.pricingTiers.free
              return {
                organizationId: orgId,
                planType: subPlanKey || subscription.planType,
                status: subscription.status || 'active',
                ...tier,
                limits: {
                  admins: tier.maxAdmins === 'custom' ? 'custom' : tier.maxAdmins,
                  regularAdmins: tier.maxRegularAdmins === 'custom' ? 'custom' : (tier.maxRegularAdmins || 0),
                  groups: tier.maxGroups === 'custom' ? 'custom' : tier.maxGroups,
                  subGroups: tier.maxSubGroups === 'custom' ? 'custom' : (tier.maxSubGroups || 0),
                  alerts: tier.maxAlertsPerMonth
                }
              }
            }
          }
          
          // Fallback: check if subscriptionId is actually a user ID and get their current subscription
          const userDoc = await getDoc(doc(db, 'users', subscriptionId))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            const userPlanKey = normalizePlanKey(userData.planType)
            if (userPlanKey && userPlanKey !== 'free') {
              const tier = this.pricingTiers[userPlanKey] || this.pricingTiers.free
              return {
                organizationId: orgId,
                planType: userPlanKey,
                status: 'active',
                ...tier,
                limits: {
                  admins: tier.maxAdmins === 'custom' ? 'custom' : tier.maxAdmins,
                  regularAdmins: tier.maxRegularAdmins === 'custom' ? 'custom' : (tier.maxRegularAdmins || 0),
                  groups: tier.maxGroups === 'custom' ? 'custom' : tier.maxGroups,
                  subGroups: tier.maxSubGroups === 'custom' ? 'custom' : (tier.maxSubGroups || 0),
                  alerts: tier.maxAlertsPerMonth
                }
              }
            }
          }
        }
      }
      
      const tier = this.pricingTiers.free
      return {
        organizationId: orgId,
        planType: 'free',
        status: 'active',
        ...tier,
        limits: {
          admins: tier.maxAdmins === 'custom' ? 'custom' : tier.maxAdmins,
          regularAdmins: tier.maxRegularAdmins === 'custom' ? 'custom' : (tier.maxRegularAdmins || 0),
          groups: tier.maxGroups === 'custom' ? 'custom' : tier.maxGroups,
          subGroups: tier.maxSubGroups === 'custom' ? 'custom' : (tier.maxSubGroups || 0),
          alerts: tier.maxAlertsPerMonth
        }
      }
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting organization subscription:', error)
      throw error
    }
  }

  // Check if user can perform an action based on their subscription
  async canUserPerformAction(userId, action, organizationId = null) {
    try {
      
      // Platform admin bypass - check if user is platform admin
      const platformAdminUIDs = ['YnFxDZkOteWCNr49v49g9AVqA9m2']
      if (platformAdminUIDs.includes(userId)) {
        return { 
          allowed: true, 
          reason: 'Platform admin - unlimited access',
          platformAdmin: true
        }
      }
      
      // First check for special access (overrides subscription limits)
      if (organizationId) {
        const specialAccess = await specialAccessService.hasSpecialAccess(userId, organizationId, action)
        if (specialAccess.hasAccess) {
          return { 
            allowed: true, 
            reason: specialAccess.reason,
            specialAccess: true,
            accessType: specialAccess.accessType
          }
        }
      }
      
      const subscription = organizationId 
        ? await this.getOrganizationSubscription(organizationId)
        : await this.getUserSubscription(userId)
      
      console.log('🔍 SubscriptionService: Checking action permission:', {
        action,
        userId,
        organizationId,
        planType: subscription.planType,
        status: subscription.status,
        tier: subscription.name
      })
      
      // Check if subscription is active
      if (subscription.status !== 'active') {
        return { allowed: false, reason: 'Subscription not active' }
      }
      
      // Check if subscription has expired
      if (subscription.currentPeriodEnd && new Date() > subscription.currentPeriodEnd.toDate()) {
        return { allowed: false, reason: 'Subscription expired' }
      }
      
      const tier = this.getPricingTier(subscription.planType)
      
      // Check specific limits
      switch (action) {
        case 'createAlert':
          const alertUsage = await this.getUsageForMonth(userId, 'alerts', organizationId)
          if (alertUsage >= tier.maxAlertsPerMonth) {
            return { 
              allowed: false, 
              reason: 'Alert limit reached', 
              limit: tier.maxAlertsPerMonth,
              used: alertUsage,
              tier: tier.name
            }
          }
          break
          
        case 'createGroup':
          const groupCount = await this.getGroupCount(organizationId)
          if (groupCount >= tier.maxGroups) {
            return { 
              allowed: false, 
              reason: 'Group limit reached', 
              limit: tier.maxGroups,
              used: groupCount,
              tier: tier.name
            }
          }
          break
          
        case 'addAdmin':
          const adminCount = await this.getAdminCount(organizationId)
          if (adminCount >= tier.maxAdmins) {
            return { 
              allowed: false, 
              reason: 'Admin limit reached', 
              limit: tier.maxAdmins,
              used: adminCount,
              tier: tier.name
            }
          }
          break
          
        case 'manageAdmins':
          
          // Allow admin management for any non-free subscription
          if (subscription.planType === 'free') {
            return { 
              allowed: false, 
              reason: 'Admin management requires a paid subscription', 
              tier: tier.name
            }
          }
          break
          
        case 'createCalendarEvent':
        case 'createScheduledAlert':
          
          // Only allow calendar access for Premium and Enterprise tiers
          if (!this.isCalendarAccessAllowed(subscription)) {
            return { 
              allowed: false, 
              reason: 'Calendar posting requires Premium or Enterprise subscription', 
              tier: tier.name
            }
          }
          break
          
        case 'viewAnalytics':
        case 'accessAnalytics':
          
          // Only allow analytics access for Premium and Enterprise tiers
          if (!this.isAnalyticsAccessAllowed(subscription)) {
            return { 
              allowed: false, 
              reason: 'Analytics dashboard requires Premium or Enterprise subscription', 
              tier: tier.name
            }
          }
          break
        default:
          return { allowed: false, reason: 'Unknown action' }
      }
      
      return { allowed: true, tier: tier.name }
      
    } catch (error) {
      console.error('❌ SubscriptionService: Error checking action permission:', error)
      return { allowed: false, reason: 'Error checking permissions' }
    }
  }

  // Get usage for current month
  async getUsageForMonth(userId, type, organizationId = null) {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7) // '2025-01'
      
      if (organizationId) {
        // Use organization usage subcollection
        const usageDoc = await getDoc(doc(db, 'organizations', organizationId, 'usage', currentMonth))
        if (usageDoc.exists()) {
          const usage = usageDoc.data()
          return usage[type] || 0
        }
      } else {
        // Fallback to user-level usage (for backward compatibility)
        const usageId = `${userId}_${currentMonth}`
        const usageDoc = await getDoc(doc(db, 'usage', usageId))
        if (usageDoc.exists()) {
          const usage = usageDoc.data()
          return usage[type] || 0
        }
      }
      
      return 0
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting usage:', error)
      return 0
    }
  }

  // Increment usage counter
  async incrementUsage(userId, type, organizationId = null) {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7)
      
      if (organizationId) {
        // Use organization usage subcollection
        const usageRef = doc(db, 'organizations', organizationId, 'usage', currentMonth)
        const usageDoc = await getDoc(usageRef)
        
        if (usageDoc.exists()) {
          await updateDoc(usageRef, {
            [type]: increment(1),
            updatedAt: serverTimestamp()
          })
        } else {
          await setDoc(usageRef, {
            organizationId,
            month: currentMonth,
            [type]: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
        }
      } else {
        // Fallback to user-level usage (for backward compatibility)
        const usageId = `${userId}_${currentMonth}`
        const usageRef = doc(db, 'usage', usageId)
        const usageDoc = await getDoc(usageRef)
        
        if (usageDoc.exists()) {
          await updateDoc(usageRef, {
            [type]: increment(1),
            updatedAt: serverTimestamp()
          })
        } else {
          await setDoc(usageRef, {
            userId,
            month: currentMonth,
            [type]: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
        }
      }
      
    } catch (error) {
      console.error('❌ SubscriptionService: Error incrementing usage:', error)
    }
  }

  // Get group count for organization
  async getGroupCount(organizationId) {
    try {
      const groupsQuery = query(collection(db, 'organizations', organizationId, 'groups'))
      const groupsSnapshot = await getDocs(groupsQuery)
      return groupsSnapshot.size
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting group count:', error)
      return 0
    }
  }

  // Get admin count for organization
  async getAdminCount(organizationId) {
    try {
      // First, check if there are admins stored in the organization document
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId))
      if (orgDoc.exists()) {
        const orgData = orgDoc.data()
        const adminIds = orgData.adminIds || {}
        const orgAdminCount = Object.keys(adminIds).filter(adminId => adminIds[adminId] === true).length
        
        // If we have admins in the org document, return that count
        if (orgAdminCount > 0) {
          return orgAdminCount
        }
      }
      
      // Fallback: count users who are organization admins for this organization
      const usersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId),
        where('isOrganizationAdmin', '==', true)
      )
      const usersSnapshot = await getDocs(usersQuery)
      
      return usersSnapshot.size
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting admin count:', error)
      return 0
    }
  }

  // Get sub-group count for organization
  async getSubGroupCount(organizationId) {
    try {
      const subGroupsQuery = query(collection(db, 'organizations', organizationId, 'subGroups'))
      const subGroupsSnapshot = await getDocs(subGroupsQuery)
      return subGroupsSnapshot.size
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting sub-group count:', error)
      return 0
    }
  }

  // Get comprehensive usage stats
  async getUsageStats(userId, organizationId = null) {
    try {
      // Platform admin bypass - check if user is platform admin
      const platformAdminUIDs = ['YnFxDZkOteWCNr49v49g9AVqA9m2']
      if (platformAdminUIDs.includes(userId)) {
        return {
          subscription: {
            planType: 'platform_admin',
            status: 'active',
            tier: 'Platform Admin',
            price: 0
          },
          usage: {
            alerts: {
              used: 0,
              limit: -1, // Unlimited
              remaining: -1, // Unlimited
              percentage: 0
            },
            groups: {
              used: 0,
              limit: -1, // Unlimited
              remaining: -1, // Unlimited
              percentage: 0
            },
            admins: {
              used: 0,
              limit: -1, // Unlimited
              remaining: -1, // Unlimited
              percentage: 0
            }
          },
          features: ['Unlimited alerts', 'Unlimited groups', 'Unlimited admins', 'Full platform access', 'All features enabled'],
          platformAdmin: true
        }
      }
      
      const subscription = organizationId 
        ? await this.getOrganizationSubscription(organizationId)
        : await this.getUserSubscription(userId)
      
      const tier = this.getPricingTier(subscription.planType)
      
      const alertUsage = await this.getUsageForMonth(userId, 'alerts', organizationId)
      const groupCount = organizationId ? await this.getGroupCount(organizationId) : 0
      const adminCount = organizationId ? await this.getAdminCount(organizationId) : 0
      const subGroupCount = organizationId ? await this.getSubGroupCount(organizationId) : 0
      
      // Calculate usage percentages and remaining
      const alertsRemaining = tier.maxAlertsPerMonth === -1 ? -1 : Math.max(0, tier.maxAlertsPerMonth - alertUsage)
      const groupsRemaining = tier.maxGroups === -1 ? -1 : Math.max(0, tier.maxGroups - groupCount)
      const adminsRemaining = tier.maxAdmins === -1 ? -1 : Math.max(0, tier.maxAdmins - adminCount)
      
      return {
        subscription: {
          planType: subscription.planType,
          status: subscription.status,
          tier: tier.name,
          price: tier.price
        },
        usage: {
          alerts: {
            used: alertUsage,
            limit: tier.maxAlertsPerMonth,
            remaining: alertsRemaining,
            percentage: tier.maxAlertsPerMonth === -1 ? 0 : Math.round((alertUsage / tier.maxAlertsPerMonth) * 100)
          },
          groups: {
            used: groupCount,
            limit: tier.maxGroups,
            remaining: groupsRemaining,
            percentage: tier.maxGroups === -1 ? 0 : Math.round((groupCount / tier.maxGroups) * 100)
          },
          admins: {
            used: adminCount,
            limit: tier.maxAdmins,
            remaining: adminsRemaining,
            percentage: tier.maxAdmins === -1 ? 0 : Math.round((adminCount / tier.maxAdmins) * 100)
          }
        },
        features: tier.features
      }
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting usage stats:', error)
      throw error
    }
  }

  // Cancel subscription
  async cancelSubscription(userId) {
    try {
      
      await updateDoc(doc(db, 'subscriptions', userId), {
        status: 'cancelled',
        cancelAtPeriodEnd: true,
        updatedAt: serverTimestamp()
      })
      
    } catch (error) {
      console.error('❌ SubscriptionService: Error cancelling subscription:', error)
      throw error
    }
  }

  // Handle trial expiration - revoke access from invited admins
  async handleTrialExpiration(userId) {
    try {
      console.log('🔍 Handling trial expiration for user:', userId)
      
      // Get user profile to find organization
      const userRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userRef)
      
      if (!userDoc.exists()) {
        console.log('❌ User not found:', userId)
        return
      }
      
      const userData = userDoc.data()
      const organizationId = userData.organizationId
      
      if (!organizationId) {
        console.log('❌ No organization found for user:', userId)
        return
      }
      
      // Find all users who are organization admins for this organization
      const usersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId),
        where('isOrganizationAdmin', '==', true)
      )
      
      const usersSnapshot = await getDocs(usersQuery)
      console.log(`🔍 Found ${usersSnapshot.docs.length} organization admins for org: ${organizationId}`)
      
      // Revoke admin access from all admins except the original creator
      const revokePromises = []
      
      for (const userDoc of usersSnapshot.docs) {
        const adminUserId = userDoc.id
        const adminData = userDoc.data()
        
        // Skip the original creator (the one whose trial expired)
        if (adminUserId === userId) {
          console.log('⏭️ Skipping original creator:', adminUserId)
          continue
        }
        
        // Revoke admin access
        revokePromises.push(
          updateDoc(doc(db, 'users', adminUserId), {
            isOrganizationAdmin: false,
            adminStatus: 'revoked',
            adminRole: 'none',
            adminPermissions: [],
            accessLevel: 'standard',
            subscriptionStatus: 'none',
            revokedAt: serverTimestamp(),
            revokedReason: 'trial_expired'
          })
        )
        
        console.log(`🔄 Revoking admin access for: ${adminUserId} (${adminData.email})`)
      }
      
      // Execute all revocations
      await Promise.all(revokePromises)
      
      // Update the original creator to standard access
      await updateDoc(doc(db, 'users', userId), {
        accessLevel: 'standard',
        subscriptionStatus: 'none',
        subscriptionType: 'none',
        trialEndDate: null,
        trialDaysRemaining: 0,
        trialExpiredAt: serverTimestamp()
      })
      
      console.log(`✅ Trial expiration handled for user ${userId} and ${revokePromises.length} invited admins`)
      
      return {
        success: true,
        revokedAdmins: revokePromises.length
      }
    } catch (error) {
      console.error('❌ Error handling trial expiration:', error)
      throw error
    }
  }

  // Get display name for subscription with trial days and billing period
  getSubscriptionDisplayName(subscription) {
    if (!subscription) return 'No Subscription'
    
    const tier = this.getPricingTier(subscription.planType)
    if (!tier) return subscription.planType || 'Unknown'
    
    console.log('🔍 getSubscriptionDisplayName input:', {
      planType: subscription.planType,
      billingPeriod: subscription.billingPeriod,
      tierName: tier.name,
      monthlyPrice: tier.monthlyPrice,
      yearlyPrice: tier.yearlyPrice
    })
    
    // If it's a trial, show days remaining
    if (tier.isTrial && subscription.trialDaysRemaining !== undefined) {
      const days = subscription.trialDaysRemaining
      if (days > 0) {
        return `${tier.name} - ${days} Day${days === 1 ? '' : 's'} Left`
      } else {
        return `${tier.name} - Expired`
      }
    }
    
    // Special case: Premium plan in trial status should show "Premium Trial"
    if (subscription.planType === 'premium' && subscription.status === 'trial' && subscription.trialDaysRemaining !== undefined) {
      const days = subscription.trialDaysRemaining
      if (days > 0) {
        return `Premium Trial - ${days} Day${days === 1 ? '' : 's'} Left`
      } else {
        return `Premium Trial - Expired`
      }
    }
    
    // Show billing period if available
    if (subscription.billingPeriod) {
      const period = subscription.billingPeriod === 'year' ? 'year' : 'month'
      // Use subscription data first, fallback to tier data
      const price = subscription.billingPeriod === 'year' 
        ? (subscription.yearlyPrice || tier.yearlyPrice)
        : (subscription.monthlyPrice || tier.monthlyPrice)
      console.log('🔍 Using billing period pricing:', { 
        period, 
        price, 
        subscriptionYearlyPrice: subscription.yearlyPrice,
        subscriptionMonthlyPrice: subscription.monthlyPrice,
        tierYearlyPrice: tier.yearlyPrice,
        tierMonthlyPrice: tier.monthlyPrice
      })
      return `${tier.name} - $${price}/${period}`
    }
    
    console.log('🔍 No billing period, using default tier name')
    return tier.name
  }

  // Check if subscription is a trial
  isTrialSubscription(subscription) {
    if (!subscription) return false
    const tier = this.getPricingTier(subscription.planType)
    return tier?.isTrial === true
  }

  // Get effective limits for a subscription (trial limits if active, fallback limits if expired)
  getEffectiveLimits(subscription) {
    if (!subscription) {
      return this.getPricingTier('free')
    }

    const tier = this.getPricingTier(subscription.planType)
    if (!tier) {
      return this.getPricingTier('free')
    }

    // If it's a trial and still active, return trial limits
    if (tier.isTrial && subscription.trialDaysRemaining > 0) {
      return tier
    }

    // If it's a trial but expired, return free tier limits
    if (tier.isTrial && subscription.trialDaysRemaining <= 0) {
      return this.getPricingTier('free')
    }

    // For non-trial subscriptions, return their actual limits
    return tier
  }

  // Check if trial is expired
  isTrialExpired(subscription) {
    if (!subscription) return false
    const tier = this.getPricingTier(subscription.planType)
    return tier?.isTrial === true && subscription.trialDaysRemaining <= 0
  }
}

export default new SubscriptionService()
